import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useDispatch, useStore } from 'react-redux'

import type { Geometry } from 'geojson'

import { setLayerFilters } from '@/actions'
import {
  applyEditingLayerFeatureFilters,
  restoreEditingLayerFeatureFiltersFromSnapshots,
  type EditingLayerFilterSnapshot,
} from '@/utilities/editingLayerFeatureFilters'

/**
 * Lightweight descriptor of a feature the user clicked to switch to while
 * already editing another one. Geometry is resolved lazily by
 * `useStartFeatureEdit` (intent `'switch'`) once the user confirms, so this
 * stays a cheap click payload — no server fetch at request time.
 */
export interface PendingSwitchTarget {
  layerId: string
  mamId: string | number
  properties: Record<string, unknown> | null
  layerLabel: string
}

const cloneStructuredDeep = <T,>(value: T): T => {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value)
    } catch {
      /* fall through — GeoJSON/properties may hit unsupported clones */
    }
  }
  return JSON.parse(JSON.stringify(value)) as T
}

/**
 * State for a single TOC-layer feature checked out for vertex editing.
 *
 * V1 supports editing **one** existing feature at a time. The user must
 * explicitly Save or Cancel before editing another feature — see
 * `EditExistingFeatureBar` for the UI contract.
 *
 * Extension to multi-feature would change `editing: T | null` to a map keyed
 * by `${layerId}:${mamId}`; the rest of the surface (sync code, filter
 * handling, save mutation) is structured so that change is local.
 */
export interface EditingLayerFeature {
  layerId: string
  mamId: string | number
  /**
   * Display label resolved at `startEdit` time (e.g. the TOC node label).
   * Pre-resolved here so consumers like the floating Save bar don't have
   * to re-walk `dataConfig.tocLayers` for every render. Falls back to
   * `layerId` at the call site when the TOC entry has no explicit label.
   */
  layerLabel: string
  /**
   * Snapshot taken at `startEdit` time. Used by:
   *   - The Save bar's "is this dirty?" check (compare vs the live Terra
   *     Draw store geometry).
   *   - The Cancel-discard confirmation gate (skip the modal when clean).
   *
   * For singleton `Multi*` features (exactly one part), this holds the flattened
   * simple shape Terra Draw edits; `singletonMultiGeometrySource` keeps the
   * original Multi envelope for save.
   *
   * Already normalized (~7 decimals) so Terra Draw accepts it directly.
   */
  originalGeometry: Geometry
  /**
   * Original GeoJSON when the tile/API returned MultiPolygon/MultiLineString/
   * MultiPoint with exactly one shell (common). Persist flows re-wrap the edited
   * simple geometry via `mergeVertexEditedSimpleIntoSingletonMulti` from
   * `@/utilities/geometry/simpleGeometry`.
   * `null` for native Point / LineString / Polygon features.
   */
  singletonMultiGeometrySource: Geometry | null
  /**
   * Snapshot of the feature's properties at edit-start. Sent through
   * unchanged on save — we only update geometry, never properties, in this
   * flow. (Property edits happen in FAP's AttributeTab via its own mutation.)
   */
  originalProperties: Record<string, unknown> | null
}

interface EditingLayerFeatureContextValue {
  editing: EditingLayerFeature | null
  /**
   * Begin an edit session. Caller is responsible for setting
   * `activeMode = 'edit-geometry'` separately — the two transitions are
   * batched by React 18 when called from the same event handler.
   *
   * @returns `false` when another feature is already checked out (caller
   *          should toast / block UX). Nested starts corrupt map filter
   *          snapshots — only one TOC edit session runs at a time.
   */
  startEdit: (feature: EditingLayerFeature) => boolean
  /**
   * Atomically swap the checked-out feature for another one. Unlike
   * {@link startEdit}, this bypasses the single-feature guard — a click-to-switch
   * fires `clear`+`start` in the same tick, where the guard would otherwise see
   * stale `editing` state and block. Restores the outgoing feature's sibling
   * filters, then applies the incoming feature's, in one render.
   *
   * Callers must resolve `originalGeometry` first (see `useStartFeatureEdit`
   * intent `'switch'`). Dirty-edit confirmation is the caller's responsibility.
   */
  switchEdit: (feature: EditingLayerFeature) => void
  /**
   * Clear the editing slot. Used by both the Save success path and the
   * Cancel path. The discard-confirm modal lives at the UI layer; by the
   * time this fires, the user has either saved or confirmed discard.
   */
  clearEdit: () => void
  /** A click-to-switch target awaiting dirty-edit confirmation, or `null`. */
  pendingSwitchTarget: PendingSwitchTarget | null
  /** Record a switch target so the floating bar can confirm discarding dirty edits. */
  requestSwitch: (target: PendingSwitchTarget) => void
  /** Drop a pending switch target (user cancelled, or it has been consumed). */
  clearPendingSwitch: () => void
  /** A click-away/cancel action awaiting dirty-edit confirmation. */
  pendingCancel: boolean
  /** Ask the floating bar to show its discard-confirm modal. */
  requestCancel: () => void
  /** Drop a pending cancel request without exiting edit mode. */
  clearPendingCancel: () => void
}

const EditingLayerFeatureContext =
  createContext<EditingLayerFeatureContextValue | null>(null)

export const EditingLayerFeatureProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const dispatch = useDispatch()
  const store = useStore<{ mapStyle: unknown }>()

  const [editing, setEditing] = useState<EditingLayerFeature | null>(null)
  const [layerFilterSnapshots, setLayerFilterSnapshots] =
    useState<EditingLayerFilterSnapshot | null>(null)
  const [pendingSwitchTarget, setPendingSwitchTarget] =
    useState<PendingSwitchTarget | null>(null)
  const [pendingCancel, setPendingCancel] = useState(false)

  /** Ref mirrors for Redux `subscribe` — updated synchronously in callbacks too. */
  const editingRef = useRef<EditingLayerFeature | null>(null)
  const layerFilterSnapshotsRef = useRef<EditingLayerFilterSnapshot | null>(
    null
  )
  // While `switchEdit` runs its restore→apply dispatch sequence, suspend the
  // Redux `subscribe` re-applier below: mid-sequence `editingRef` still points
  // at the outgoing feature, so the subscriber would re-exclude it (and its
  // snapshot is about to be dropped, so it would never be restored).
  const isApplyingFiltersRef = useRef(false)

  editingRef.current = editing
  layerFilterSnapshotsRef.current = layerFilterSnapshots

  const dispatchRestoredLayersIfNeeded = useCallback(
    (snapshots: EditingLayerFilterSnapshot) => {
      const { filterEntries, shouldDispatch } =
        restoreEditingLayerFeatureFiltersFromSnapshots({
          mapStyleRaw: store.getState().mapStyle,
          snapshots,
        })
      if (shouldDispatch) {
        dispatch(setLayerFilters(filterEntries))
      }
    },
    [dispatch, store]
  )

  const startEdit = useCallback(
    (feature: EditingLayerFeature): boolean => {
      if (editingRef.current !== null) return false

      const nextEditing: EditingLayerFeature = {
        layerId: feature.layerId,
        mamId: feature.mamId,
        layerLabel: feature.layerLabel,
        originalGeometry: cloneStructuredDeep(feature.originalGeometry),
        singletonMultiGeometrySource:
          feature.singletonMultiGeometrySource === null
            ? null
            : cloneStructuredDeep(feature.singletonMultiGeometrySource),
        originalProperties:
          feature.originalProperties === null
            ? null
            : cloneStructuredDeep(feature.originalProperties),
      }

      const { nextSnapshots, filterEntries, shouldDispatch } =
        applyEditingLayerFeatureFilters({
          mapStyleRaw: store.getState().mapStyle,
          editing: {
            layerId: nextEditing.layerId,
            mamId: nextEditing.mamId,
          },
          persistedSnapshots: {},
        })

      editingRef.current = nextEditing
      layerFilterSnapshotsRef.current = nextSnapshots

      if (shouldDispatch) {
        dispatch(setLayerFilters(filterEntries))
      }

      setLayerFilterSnapshots(nextSnapshots)
      setEditing(nextEditing)
      setPendingCancel(false)
      return true
    },
    [dispatch, store]
  )

  const switchEdit = useCallback(
    (feature: EditingLayerFeature): void => {
      const nextEditing: EditingLayerFeature = {
        layerId: feature.layerId,
        mamId: feature.mamId,
        layerLabel: feature.layerLabel,
        originalGeometry: cloneStructuredDeep(feature.originalGeometry),
        singletonMultiGeometrySource:
          feature.singletonMultiGeometrySource === null
            ? null
            : cloneStructuredDeep(feature.singletonMultiGeometrySource),
        originalProperties:
          feature.originalProperties === null
            ? null
            : cloneStructuredDeep(feature.originalProperties),
      }

      // Suspend the Redux `subscribe` re-applier for the whole restore→apply
      // sequence. The restore dispatch fires `subscribe` while `editingRef`
      // still points at the OUTGOING feature, so without this guard the
      // subscriber would immediately re-exclude it — and since its snapshot is
      // dropped on the next line, nothing would ever restore it (the outgoing
      // feature would vanish permanently, even across a visibility toggle).
      isApplyingFiltersRef.current = true
      try {
        // 1. Revert the outgoing feature's sibling filters to baseline. Redux
        //    dispatch is synchronous, so the apply() below reads the restored
        //    style and re-snapshots a clean baseline for the incoming feature.
        const prevSnapshots = layerFilterSnapshotsRef.current
        if (prevSnapshots && Object.keys(prevSnapshots).length > 0) {
          const { filterEntries, shouldDispatch } =
            restoreEditingLayerFeatureFiltersFromSnapshots({
              mapStyleRaw: store.getState().mapStyle,
              snapshots: prevSnapshots,
            })
          if (shouldDispatch) {
            dispatch(setLayerFilters(filterEntries))
          }
        }

        // 2. Apply the incoming feature's exclusion from the clean baseline.
        const { nextSnapshots, filterEntries, shouldDispatch } =
          applyEditingLayerFeatureFilters({
            mapStyleRaw: store.getState().mapStyle,
            editing: {
              layerId: nextEditing.layerId,
              mamId: nextEditing.mamId,
            },
            persistedSnapshots: {},
          })

        editingRef.current = nextEditing
        layerFilterSnapshotsRef.current = nextSnapshots

        if (shouldDispatch) {
          dispatch(setLayerFilters(filterEntries))
        }

        setLayerFilterSnapshots(nextSnapshots)
        setEditing(nextEditing)
        setPendingSwitchTarget(null)
        setPendingCancel(false)
      } finally {
        isApplyingFiltersRef.current = false
      }
    },
    [dispatch, store]
  )

  const clearEdit = useCallback(() => {
    const snapshots = layerFilterSnapshotsRef.current

    editingRef.current = null
    layerFilterSnapshotsRef.current = {}

    if (snapshots && Object.keys(snapshots).length > 0) {
      dispatchRestoredLayersIfNeeded(snapshots)
    }

    setLayerFilterSnapshots(null)
    setEditing(null)
    setPendingSwitchTarget(null)
    setPendingCancel(false)
  }, [dispatchRestoredLayersIfNeeded])

  const requestSwitch = useCallback((target: PendingSwitchTarget) => {
    setPendingSwitchTarget(target)
  }, [])

  const clearPendingSwitch = useCallback(() => {
    setPendingSwitchTarget(null)
  }, [])

  const requestCancel = useCallback(() => {
    setPendingCancel(true)
  }, [])

  const clearPendingCancel = useCallback(() => {
    setPendingCancel(false)
  }, [])

  /**
   * When the mapStyle changes mid-edit (tile bust, style refresh, etc.),
   * sibling-layer filters carrying the exclusion may disappear. Redux
   * `subscribe` is an explicit external subscription (not React
   * state sync): we re-compose from the persisted snapshot keyed by edit
   * lifecycle.
   */
  useEffect(() => {
    let prevMapStyle = store.getState().mapStyle
    const unsub = store.subscribe(() => {
      const mapStyleNow = store.getState().mapStyle
      if (mapStyleNow === prevMapStyle) return
      prevMapStyle = mapStyleNow

      // `switchEdit` drives its own restore→apply dispatches; suppress the
      // re-applier so it doesn't re-exclude the outgoing feature mid-sequence.
      if (isApplyingFiltersRef.current) return

      const editingCurrent = editingRef.current
      const persisted = layerFilterSnapshotsRef.current

      if (
        !editingCurrent ||
        !persisted ||
        Object.keys(persisted).length === 0
      ) {
        return
      }

      const { nextSnapshots, filterEntries, shouldDispatch } =
        applyEditingLayerFeatureFilters({
          mapStyleRaw: mapStyleNow,
          editing: {
            layerId: editingCurrent.layerId,
            mamId: editingCurrent.mamId,
          },
          persistedSnapshots: persisted,
        })

      layerFilterSnapshotsRef.current = nextSnapshots

      const prevKeySig = Object.keys(persisted).sort().join('|')
      const nextKeySig = Object.keys(nextSnapshots).sort().join('|')
      if (prevKeySig !== nextKeySig) {
        setLayerFilterSnapshots(nextSnapshots)
      }

      if (shouldDispatch) {
        dispatch(setLayerFilters(filterEntries))
      }
    })
    return () => {
      unsub()
      const snaps = layerFilterSnapshotsRef.current
      if (editingRef.current && snaps && Object.keys(snaps).length > 0) {
        editingRef.current = null
        layerFilterSnapshotsRef.current = {}
        dispatchRestoredLayersIfNeeded(snaps)
      }
    }
  }, [dispatch, dispatchRestoredLayersIfNeeded, store])

  const value = useMemo<EditingLayerFeatureContextValue>(
    () => ({
      editing,
      startEdit,
      switchEdit,
      clearEdit,
      pendingSwitchTarget,
      requestSwitch,
      clearPendingSwitch,
      pendingCancel,
      requestCancel,
      clearPendingCancel,
    }),
    [
      editing,
      startEdit,
      switchEdit,
      clearEdit,
      pendingSwitchTarget,
      requestSwitch,
      clearPendingSwitch,
      pendingCancel,
      requestCancel,
      clearPendingCancel,
    ]
  )

  return (
    <EditingLayerFeatureContext.Provider value={value}>
      {children}
    </EditingLayerFeatureContext.Provider>
  )
}

export const useEditingLayerFeature = (): EditingLayerFeatureContextValue => {
  const ctx = useContext(EditingLayerFeatureContext)
  if (!ctx) {
    throw new Error(
      'useEditingLayerFeature must be used within EditingLayerFeatureProvider'
    )
  }
  return ctx
}

/** Safe when the provider is absent (e.g. tests / non-map routes). */
export const useEditingLayerFeatureOptional =
  (): EditingLayerFeatureContextValue | null =>
    useContext(EditingLayerFeatureContext)
